<template>
  <div>
    <transition-group
      name="list"
      mode="out-in"
      tag="div"
      class="amount-input"
    >
      <amount-variable-input
        v-if="selectedItemIsVariable"
        key="variable"
        v-model="amount"
        :item="selectedItem"
      />

      <amount-fixed-input
        v-else-if="fixedValueOptions.length"
        key="fixed"
        v-model="utid"
        :item-options="fixedValueOptions"
        :selected-item="selectedItem"
      />
    </transition-group>

    <div class="fee-message animated fadeIn">
      {{ feeMessage }}
    </div>

    <display-if-catalog-has-physical-items>
      <div
        :aria-label="$t('delivery-type')"
        role="radiogroup"
      >
        <div
          v-for="fulfillmentType in fulfillmentTypes"
          :key="fulfillmentType"
          :class="{ active: fulfillmentType === selectedFulfillmentType }"
          class="fulfillment-type"
        >
          <label
            class="btn-block label-text"
            :for="`fulfillment-type-${fulfillmentType}`"
          >
            {{ getFulfillmentTypeLabel(fulfillmentType) }}
          </label>
          <input
            :id="`fulfillment-type-${fulfillmentType}`"
            :checked="
              fulfillmentType === selectedFulfillmentType ||
                fulfillmentTypes.length === 1
            "
            type="radio"
            name="fulfillment-type"
            autocomplete="off"
            @change="setSelectedFulfillmentType(fulfillmentType)"
          >
        </div>
      </div>
    </display-if-catalog-has-physical-items>
  </div>
</template>

<script>
import AmountFixedInput from './AmountFixedInput'
import AmountVariableInput from './AmountVariableInput'
import {
  FulfillmentType,
  ItemRewardValueType,
  AdjustmentType
} from '@/cowboy/EnumTypes'
import _ from 'lodash'
import DisplayIfCatalogHasPhysicalItems from '../../shared/catalog/DisplayIfCatalogHasPhysicalItems'

export default {
  name: 'ItemSelector',
  components: {
    DisplayIfCatalogHasPhysicalItems,
    AmountFixedInput,
    AmountVariableInput
  },
  inject: ['$validator'],
  props: {
    brand: {
      type: Object,
      required: true
    },
    previousAmount: {
      type: Number,
      required: false,
      default: undefined
    },
    previousUtid: {
      type: String,
      required: false,
      default: undefined
    },
    previousFulfillmentType: {
      type: String,
      required: true,
      validator: prop => Object.values(FulfillmentType).includes(prop)
    },
    value: {
      type: [Object, String],
      required: false,
      default: ''
    }
  },
  data: () => ({
    amount: null,
    utid: null,
    selectedFulfillmentType: null
  }),
  computed: {
    feeMessage () {
      const {
        selectedItem: { adjustment, currencyCode } = {},
        $t,
        $currencySymbol
      } = this

      let feeMessage
      if (adjustment && adjustment.value) {
        if (adjustment.type === AdjustmentType.FLAT) {
          const itemFeeAmount = adjustment.value
            .toFixed(2)
            .toString()
            .replace('.00', '')
          feeMessage = `${$t('reward-fee-message-a')} ${$currencySymbol(
            currencyCode
          )}${itemFeeAmount} ${$t('reward-fee-message')}`
        } else {
          const itemFeePct = (adjustment.value * 100.0).toString()
          feeMessage = `${$t('reward-fee-message-a')} ${itemFeePct}% ${$t(
            'reward-fee-message'
          )}`
        }
      }

      return feeMessage
    },
    fixedValueOptions () {
      const { selectedItemOptions } = this
      return selectedItemOptions
        .filter(({ valueType }) => valueType === ItemRewardValueType.FIXED_VALUE)
        .map(({ faceValue, currencyCode, isAvailable, utid }) => ({
          faceValue,
          currencyCode,
          disabled: !isAvailable,
          utid
        }))
    },
    availableFixedValueOptions () {
      return this.fixedValueOptions.filter(({ disabled }) => !disabled)
    },
    fixedValueOptionFromAmount () {
      return this.availableFixedValueOptions.filter(
        ({ faceValue }) => faceValue === parseFloat(this.amount)
      )
    },
    fulfillmentTypes () {
      const { items } = this
      return _.uniq(items.map(({ fulfillmentType }) => fulfillmentType)).sort()
    },
    items () {
      const {
        brand: { items = [] }
      } = this
      return items
    },
    selectedItem () {
      const {
        selectedItemOptions,
        selectedItemIsVariable,
        selectedFixedItem
      } = this
      const [firstItem = {}] = selectedItemOptions
      return selectedItemIsVariable ? firstItem : selectedFixedItem
    },
    selectedFixedItem () {
      const { selectedItemOptions } = this
      const availableSelectedItemOptions = selectedItemOptions.filter(
        ({ isAvailable }) => isAvailable
      )
      return (
        availableSelectedItemOptions.find(({ utid }) => utid === this.utid) ||
        availableSelectedItemOptions[0] ||
        {}
      )
    },
    selectedItemOptions () {
      const { selectedFulfillmentType, items } = this
      return items
        .filter(
          ({ fulfillmentType }) => fulfillmentType === selectedFulfillmentType
        )
        .slice()
        .sort(
          (item, otherItem) =>
            parseFloat(item.faceValue) - parseFloat(otherItem.faceValue)
        )
    },
    selectedItemIsVariable () {
      const { selectedItemOptions } = this
      return (
        selectedItemOptions.length &&
        selectedItemOptions[0].valueType === ItemRewardValueType.VARIABLE_VALUE
      )
    }
  },
  watch: {
    selectedFulfillmentType () {
      this.setAmountToDefault()
    },
    selectedItem () {
      this.valueUpdated()
    },
    amount () {
      this.valueUpdated()
    },
    utid () {
      if (
        this.selectedItemIsVariable ||
        !this.utid ||
        !this.fixedValueOptions.length
      ) {
        return
      }
      const { faceValue = 0 } =
        this.fixedValueOptions.find(option => option.utid === this.utid) || {}
      this.amount = faceValue
    },
    brand () {
      this.setAmountToDefault()
      this.setDefaultFulfillmentType()
    },
    selectedFixedItem () {
      if (!this.selectedItemIsVariable) {
        this.utid = this.selectedFixedItem.utid
      }
    }
  },
  mounted () {
    this.setInitialFulfillmentType()
    this.setInitialAmount()
    this.setInitialUtid()
  },
  methods: {
    getFulfillmentTypeLabel (fulfillmentType) {
      const { $t } = this
      return fulfillmentType === FulfillmentType.PHYSICAL
        ? $t('reward-physical-fulfillment')
        : $t('reward-digital-fulfillment')
    },
    valueUpdated () {
      this.$nextTick(() => this.$validator.validateAll())
      const { selectedItem: item, amount } = this
      this.$emit('change', { item, amount })
    },
    setAmountToDefault () {
      let defaultFixedValueOptions
      if (
        Array.isArray(this.fixedValueOptionFromAmount) &&
        this.fixedValueOptionFromAmount.length
      ) {
        // If amount is set (via variable input) see if we can match fixed item with same amount
        defaultFixedValueOptions = this.fixedValueOptionFromAmount
      } else {
        // Otherwise use full list of available fixed items
        defaultFixedValueOptions = this.availableFixedValueOptions
      }
      // get values from 1st item in fixed item list
      const [{ faceValue, utid } = {}] = defaultFixedValueOptions
      this.amount = this.selectedItemIsVariable ? null : faceValue
      this.utid = this.selectedItemIsVariable ? null : utid
      this.$nextTick(() => this.$validator.reset())
    },
    setInitialFulfillmentType () {
      const { fulfillmentTypes = [], previousFulfillmentType = '' } = this
      if (fulfillmentTypes.includes(previousFulfillmentType)) {
        this.setSelectedFulfillmentType(previousFulfillmentType)
      } else {
        this.setDefaultFulfillmentType()
      }
    },
    setDefaultFulfillmentType () {
      const { fulfillmentTypes = [] } = this
      const [firstFulfillmentTypeFound] = fulfillmentTypes
      this.setSelectedFulfillmentType(firstFulfillmentTypeFound)
    },
    async setInitialAmount () {
      const { previousAmount } = this
      if (previousAmount) {
        await this.$nextTick()
        this.amount = previousAmount.toString()
      }
    },
    async setInitialUtid () {
      const { previousUtid } = this
      if (previousUtid) {
        await this.$nextTick()
        this.utid = previousUtid
      }
    },
    setSelectedFulfillmentType (fulfillmentType) {
      this.selectedFulfillmentType = fulfillmentType
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../assets/css/variables';
.fulfillment-type {
  background-color: $tangoWhite;
  text-align: left;
  margin-top: 10px;
  padding: 12px 10px 8px;
  border: 2px solid transparent;
  color: $tangoTextPrimary;
  font-size: $font-size-md;
  cursor: pointer;

  &.active {
    border-color: $tangoBlue;
  }
}

.amount-input {
  position: relative;
}

.fee-message {
  color: $tangoDangerRed;
  font-size: $font-size-md;
  margin-top: 5px;
}

@media screen and (max-width: $breakpoint-md - 1) {
  .fulfillment-type {
    border: 1px solid $tangoBlueGrayDk;
  }

  .label-text {
    margin-bottom: 0;
  }
}
</style>
